import React, { useEffect, useState } from "react";
import { useSession } from "../lib/session";
import { supabase } from "../lib/supabase";
import BrandShell from "../components/BrandShell";
import CreateSociety from "./CreateSociety";

/**
 * Welcome. Shown after signing in, and whenever a member chooses to
 * switch society. Lists every society they belong to with what's live in
 * each, so the choice is informed rather than just a list of names.
 */
export default function Welcome() {
  const { profile, societies, lastSocietyId, chooseSociety, signOut } = useSession();
  const [live, setLive] = useState({});       // society id -> { comps, due }
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!profile || societies.length === 0) return;
    const ids = societies.map((s) => s.id);

    Promise.all([
      supabase.from("competitions").select("id, society_id, status")
        .in("society_id", ids).neq("status", "completed"),
      supabase.from("round_entries")
        .select("id, card_status, rounds!inner(society_id, status)")
        .eq("profile_id", profile.id)
        .in("card_status", ["not_started", "in_progress"])
        .in("rounds.status", ["scoring", "in_play"]),
    ]).then(([c, d]) => {
      const out = {};
      ids.forEach((id) => { out[id] = { comps: 0, due: 0 }; });
      (c.data ?? []).forEach((r) => { if (out[r.society_id]) out[r.society_id].comps++; });
      (d.data ?? []).forEach((r) => {
        const sid = r.rounds?.society_id;
        if (out[sid]) out[sid].due++;
      });
      setLive(out);
    });
  }, [profile, societies]);

  if (adding) return <CreateSociety onBack={() => setAdding(false)} />;

  const first = (profile?.full_name ?? "").split(/\s+/)[0];
  // last opened first, the rest in the order they were joined
  const ordered = [...societies].sort((a, b) =>
    (b.id === lastSocietyId) - (a.id === lastSocietyId));

  return (
    <BrandShell>
      <div className="welcome">
        <h1>{first ? `Welcome back, ${first}` : "Welcome back"}</h1>
        <p>
          {societies.length === 1
            ? "Open your society to carry on."
            : `You're in ${societies.length} societies. Which one would you like to open?`}
        </p>

        <div className="soc-list">
          {ordered.map((s) => {
            const info = live[s.id];
            return (
              <button key={s.id} className="soc" onClick={() => chooseSociety(s.id)}>
                <span className="soc-main">
                  <span className="soc-name">{s.name}</span>
                  <span className="soc-meta">
                    {s.role !== "player" && <span className="pill pill-mark">{s.role}</span>}
                    {s.id === lastSocietyId && societies.length > 1 && (
                      <span className="soc-last">Last opened</span>
                    )}
                    {info && (
                      <span>
                        {info.comps === 0 ? "Nothing on at the moment"
                          : `${info.comps} competition${info.comps === 1 ? "" : "s"} on`}
                      </span>
                    )}
                  </span>
                </span>
                {info?.due > 0 && (
                  <span className="soc-due">
                    {info.due} card{info.due === 1 ? "" : "s"} due
                  </span>
                )}
                <span className="soc-go" aria-hidden="true" />
              </button>
            );
          })}
        </div>

        <div style={{ marginTop: "1.25rem", display: "flex", flexDirection: "column", gap: ".6rem" }}>
          <button type="button" className="btn-link" onClick={() => setAdding(true)}>
            Join or start another society
          </button>
          <button type="button" className="btn-link" style={{ color: "var(--dim)" }}
                  onClick={signOut}>
            Sign out
          </button>
        </div>
      </div>
    </BrandShell>
  );
}
